import { useCallback, useEffect, useState } from 'react';
import {
  COMPANY_NAME, SECTOR, SIZE_MAX, SIZE_MIN, STOCK_TICKER, WEBSITE,
} from 'constants/formFields';
import { useLazyGetCompanyDetailsCompetitorQuery, useLazyGetCompanySearchQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useDebouncedCallback from './useDebouncedCallback';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';
import type ICompanyDetails from 'store/slices/materialAssessment/interfaces/ICompanyDetails';
import type ICompanyItem from 'store/slices/materialAssessment/interfaces/ICompanyItem';
import type { IUseCompanyCompetitors } from './interfaces/IUseCompanyCompetitors';
import type { TAsyncCompaniesCompetitorResolve } from './interfaces/TAsyncCompaniesCompetitorResolve';
import type { TAsyncSelectResolve } from './interfaces/TAsyncSelectResolve';

// The value type `setValue` accepts, derived from RHF's own signature
type TSetValueArg<T extends FieldValues> = Parameters<UseFormSetValue<T>>[1];

const useCompanyCompetitor = <T extends FieldValues>({ watch, setValue }: IUseCompanyCompetitors<T>) => {
  const [companyDetails, setCompanyDetails] = useState<ICompanyDetails | undefined>(undefined);

  const [getCompanies, { isFetching: loadingCompanies }] = useLazyGetCompanySearchQuery();
  const [getCompanyDetails, { isLoading: loadingCompanyDetails }] = useLazyGetCompanyDetailsCompetitorQuery();

  const companyNameField = COMPANY_NAME as unknown as Path<T>;
  const stockTickerField = STOCK_TICKER as unknown as Path<T>;
  const websiteField = WEBSITE as unknown as Path<T>;
  const sectorField = SECTOR as unknown as Path<T>;
  const sizeMaxField = SIZE_MAX as unknown as Path<T>;
  const sizeMinField = SIZE_MIN as unknown as Path<T>;
  const emptyOption = null as unknown as TSetValueArg<T>;

  const geoCompanyNameDebounced = useDebouncedCallback(
    (inputValue: string, resolve: TAsyncSelectResolve) => {
      getCompanies({ search: inputValue }).unwrap().then((response) => {
        const companies: ICompanyItem[] = Array.isArray(response?.data) ? response.data : [];
        const countriesOptions: IOption[] = companies.map(
          (item: ICompanyItem) => ({ label: item.companyName, value: item.companyName }),
        );
        resolve(countriesOptions);
      });
    },
  );

  const geoCompanyDetailsDebounced = useDebouncedCallback(
    (company: string, resolve: TAsyncCompaniesCompetitorResolve) => {
      getCompanyDetails({ companyNames: [company] }).unwrap().then((data) => {
        const detailsOptions: ICompanyDetails[] = data;
        if (detailsOptions.length > 0) {
          setValue(websiteField, detailsOptions[0]?.website as unknown as TSetValueArg<T>);
          setValue(sizeMaxField, detailsOptions[0]?.employeeCount as unknown as TSetValueArg<T>);
          setCompanyDetails(detailsOptions[0]);
        }
        resolve(detailsOptions);
      });
    },
  );

  const onFindCompaniesCompetitor = (inputValue: string): Promise<IOption[]> => new Promise((resolve) => {
    if (inputValue) {
      geoCompanyNameDebounced(inputValue, resolve);
    } else {
      resolve([]);
    }
  });

  const onFindCompaniesCompetitorDetails = (): Promise<ICompanyDetails[]> => new Promise((resolve) => {
    const company = watch(companyNameField)?.value;
    if (company) {
      geoCompanyDetailsDebounced(company, resolve);
    } else {
      resolve([]);
    }
  });

  const onChangeCompany = () => {
    setCompanyDetails(undefined);
    setValue(stockTickerField, emptyOption);
    setValue(websiteField, undefined as unknown as TSetValueArg<T>);
    setValue(sectorField, emptyOption);
    setValue(sizeMaxField, undefined as unknown as TSetValueArg<T>);
    setValue(sizeMinField, undefined as unknown as TSetValueArg<T>);
    onFindCompaniesCompetitorDetails();
  };

  const resetCompanyDetails = useCallback(() => {
    setCompanyDetails(undefined);
    setValue(stockTickerField, emptyOption);
    setValue(websiteField, undefined as unknown as TSetValueArg<T>);
    setValue(sectorField, emptyOption);
    setValue(sizeMaxField, undefined as unknown as TSetValueArg<T>);
    setValue(sizeMinField, undefined as unknown as TSetValueArg<T>);
  }, [setValue, websiteField, sizeMaxField, stockTickerField, sectorField, sizeMinField]);

  useEffect(() => {
    if (loadingCompanies) {
      resetCompanyDetails();
    }
  }, [loadingCompanies, resetCompanyDetails]);

  const loadingDetails = loadingCompanyDetails || loadingCompanies;

  return {
    onChangeCompany,
    onFindCompaniesCompetitor,
    companyDetails,
    loadingDetails,
    loadingCompanies,
    onFindCompaniesCompetitorDetails,
    resetCompanyDetails,
  };
};

export default useCompanyCompetitor;
